import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  Switch,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import analyticsService from '../../services/analyticsService';
import StudentLayout from '../../components/layout/StudentLayout';
import ParentLayout from '../../components/layout/ParentLayout';
import { getCurrentSubscription } from '../../services/subscriptionService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { TEACHERS_STUDENTS, EXAM_MODES, PARENT_CHILDREN } from '../../config/Config';
import api from '../../services/api';
import AnalyticsDashboard from '../../components/analytics/AnalyticsDashboard';

const StudentAnalyticsDashboard = ({ studentId = null, demoMode: externalDemoMode = false }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date;
  });
  const [toDate, setToDate] = useState(() => new Date());
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedExamMode, setSelectedExamMode] = useState('all');
  const [demoMode, setDemoMode] = useState(externalDemoMode);
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Student selection state for Teacher/Parent roles
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(studentId);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  
  // Exam modes state
  const [availableExamModes, setAvailableExamModes] = useState([]);
  const [examModesLoading, setExamModesLoading] = useState(false);
  const isParent = !!(user && (user.role === 'parent' || user.isParent || user.is_parent));
  const LayoutComponent = isParent ? ParentLayout : StudentLayout;

  const needsStudentPicker =
    !!user && (user.role === 'teacher' || user.role === 'parent') && !demoMode;
  const waitingForStudents =
    hasAnalyticsAccess && needsStudentPicker && studentsLoading;
  const noStudentsForPicker =
    hasAnalyticsAccess &&
    needsStudentPicker &&
    !studentsLoading &&
    availableStudents.length === 0;

  const checkSubscriptionAccess = useCallback(async () => {
    try {
      setSubscriptionLoading(true);
      
      // If demo mode is active, grant access immediately
      if (demoMode) {
        setHasAnalyticsAccess(true);
        setSubscriptionLoading(false);
        return;
      }
      
      const subscription = await getCurrentSubscription();
      
      let hasAccess = false;
      
      if (user) {
        if (user.role === 'student') {
          // For students, check for Family Pro plan
          hasAccess = subscription && subscription.plan_code === 'family_pro';
        } else if (user.role === 'teacher') {
          // For teachers, check for teacher_pro plan
          hasAccess = subscription && subscription.plan_code === 'teacher_pro';
        } else if (user.role === 'parent') {
          // For parents, check for Family Pro (or teacher_pro) plan
          hasAccess = subscription && (subscription.plan_code === 'family_pro' || subscription.plan_code === 'teacher_pro');
        }
      }
      
      setHasAnalyticsAccess(hasAccess);
    } catch (error) {
      console.error('Error checking subscription access:', error);
      setHasAnalyticsAccess(false);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [demoMode, user]);

  // Fetch available exam modes
  const fetchAvailableExamModes = useCallback(async () => {
    try {
      setExamModesLoading(true);
      const response = await api.get(EXAM_MODES);
      const examModes = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setAvailableExamModes(examModes);
    } catch (err) {
      console.error('Error fetching exam modes:', err);
    } finally {
      setExamModesLoading(false);
    }
  }, []);

  // Fetch available students for Teacher/Parent roles
  const fetchAvailableStudents = useCallback(async () => {
    if (!user || (user.role !== 'teacher' && user.role !== 'parent')) {
      return;
    }

    try {
      setStudentsLoading(true);
      setStudentsError(null);
      
      let transformedStudents = [];

      if (user.role === 'parent') {
        const response = await api.get(PARENT_CHILDREN);
        const raw = Array.isArray(response.data) ? response.data : [];

        transformedStudents = raw
          .filter((child) => child.status === 'active')
          .map((child) => {
            const fullName = [child.first_name, child.last_name].filter(Boolean).join(' ').trim();
            return {
              id: child.relationship_id || child.id,
              student_id: child.id,
              name: fullName || child.email || 'Child',
              email: child.email || '',
              subject: 'My Child',
            };
          });
      } else {
        const response = await api.get(TEACHERS_STUDENTS);
        const raw = Array.isArray(response.data) ? response.data : (response.data.results || response.data.students || []);

        transformedStudents = raw
          .filter(r => r.is_active && r.is_accepted)
          .map(r => {
            const studentName = r.student_name;
            
            return {
              id: r.id,
              student_id: r.student,
              name: studentName || r.student_email || 'Unknown Student',
              email: r.student_email || '',
              subject: r.subject_name || r.subject?.name || 'No Subject'
            };
          });
      }
      
      setAvailableStudents(transformedStudents);
      
      // Auto-select first student if none selected
      if (!selectedStudentId && transformedStudents.length > 0) {
        setSelectedStudentId(transformedStudents[0].student_id);
      }
    } catch (err) {
      console.error('Error fetching available students:', err);
      setStudentsError(user?.role === 'parent' ? 'Failed to fetch your children' : 'Failed to fetch students');
    } finally {
      setStudentsLoading(false);
    }
  }, [user, selectedStudentId]);

  useEffect(() => {
    checkSubscriptionAccess();
  }, [checkSubscriptionAccess]);

  // Fetch available students for Teacher/Parent roles
  useEffect(() => {
    fetchAvailableStudents();
  }, [fetchAvailableStudents]);

  // Fetch available exam modes
  useEffect(() => {
    fetchAvailableExamModes();
  }, [fetchAvailableExamModes]);

  useEffect(() => {
    if (!hasAnalyticsAccess) return;

    if (needsStudentPicker) {
      if (studentsLoading) return;
      if (availableStudents.length === 0) {
        setLoading(false);
        setError(null);
        setAnalyticsData(null);
        return;
      }
      if (!selectedStudentId) return;
    }

    fetchAnalyticsData();
  }, [
    hasAnalyticsAccess,
    needsStudentPicker,
    studentsLoading,
    availableStudents.length,
    selectedStudentId,
    fromDate,
    toDate,
    selectedTopic,
    selectedExamMode,
    demoMode,
  ]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine which student ID to use
      let effectiveStudentId;
      if (demoMode) {
        effectiveStudentId = 'demo@student.com';
      } else if (user && (user.isTeacher || user.is_teacher || user.isParent || user.is_parent)) {
        // For Teacher/Parent roles, use selected student or fallback to prop
        effectiveStudentId = selectedStudentId || studentId;
      } else {
        // For Student role, use the prop or current user's ID
        effectiveStudentId = studentId || user?.id;
      }
      
      if (!effectiveStudentId) {
        setLoading(false);
        setError(null);
        setAnalyticsData(null);
        return;
      }
      
      const data = await analyticsService.getStudentDashboard(
        effectiveStudentId,
        fromDate,
        toDate,
        selectedTopic,
        selectedExamMode,
        demoMode
      );
      
      setAnalyticsData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const centeredProgress = (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" aria-busy="true">
      <CircularProgress />
    </Box>
  );

  // Get all available topics for the filter dropdown (only when data exists)
  const getAvailableTopics = () => {
    if (!analyticsData?.topic_difficulty_breakdown?.topic_mastery) {
      return [];
    }
    const data = analyticsData.topic_difficulty_breakdown.topic_mastery;
    return [...new Set(data.map(item => item.topic))].sort();
  };

  const availableTopics = analyticsData ? getAvailableTopics() : [];

  let mainContent;

  if (subscriptionLoading) {
    mainContent = centeredProgress;
  } else if (!hasAnalyticsAccess && !demoMode) {
    mainContent = (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={8}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Analytics Access Restricted
          </Typography>
          <Typography variant="h6" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
            {user?.role === 'teacher' ? 'Upgrade to Teacher Pro plan to access insights' :
              user?.role === 'parent' ? 'Upgrade to Parent Pro plan to access progress reports' :
                'Upgrade to Student Pro plan to access insights'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
            {user?.role === 'teacher' ? 'Get detailed class performance analytics, student insights, and subject breakdowns to improve your teaching effectiveness.' :
              user?.role === 'parent' ? 'Get child-specific performance reports, progress tracking, and recommendations for your linked family account.' :
                'Get detailed performance analytics, topic mastery insights, and personalized recommendations to improve your learning journey.'}
            Enable Demo mode to check the functionality.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={Link}
              to="/profile"
              sx={{ mr: 2 }}
            >
              Go to Profile → My Subscription
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                setDemoMode(true);
                setHasAnalyticsAccess(true);
              }}
            >
              Try Demo Mode
            </Button>
          </Box>

          <Alert severity="info" sx={{ maxWidth: 600 }}>
            <Typography variant="body2">
              <strong>Demo Mode:</strong> Experience the full analytics dashboard with sample data.
              Your actual {user?.role === 'teacher' ? 'class' : user?.role === 'parent' ? 'child\'s' : ''} data will be available after upgrading to {user?.role === 'teacher' ? 'Teacher Pro' : user?.role === 'parent' ? 'Parent Pro' : 'Student Pro'}.
            </Typography>
          </Alert>
        </Box>
      </Container>
    );
  } else if (waitingForStudents) {
    mainContent = centeredProgress;
  } else if (noStudentsForPicker) {
    mainContent = (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isParent ? 'Progress Reports' : 'Student Performance Analytics'}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {isParent
            ? 'No linked children are available yet. Add a child from My Children to view progress reports, or use demo mode to preview the dashboard.'
            : 'No students available. Add students to your subjects to view analytics, or use demo mode to preview the dashboard.'}
        </Alert>
        <Button variant="outlined" color="primary" onClick={() => setDemoMode(true)}>
          Try Demo Mode
        </Button>
      </Container>
    );
  } else if (loading) {
    mainContent = centeredProgress;
  } else if (error) {
    mainContent = (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Container>
    );
  } else if (!analyticsData) {
    mainContent = centeredProgress;
  } else {
    mainContent = (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 3, md: 6 }, px: { xs: 1.5, sm: 2 } }}>
        {/* Header */}
        <Box mb={{ xs: 2, md: 4 }}>
          {demoMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Demo Mode Active:</strong> You are now viewing sample data for demonstration purposes. 
                Toggle off to view your actual {user?.role === 'teacher' ? 'class' : user?.role === 'parent' ? 'child\'s' : ''} performance data.
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {demoMode ? 'Demo User Analytics' : isParent ? 'Progress Reports' : 'Student Performance Analytics'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {demoMode
                  ? 'demo@student.com'
                  : (analyticsData.student_info?.name || 'Your')
                } {isParent ? 'progress' : 'performance analysis'} from {fromDate.toLocaleDateString()} to {toDate.toLocaleDateString()}
              </Typography>
            </Box>
            
            {/* Demo Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={demoMode}
                  onChange={(e) => {
                    setDemoMode(e.target.checked);
                    if (e.target.checked) {
                      // When demo mode is enabled, allow access to analytics
                      setHasAnalyticsAccess(true);
                    } else {
                      // When demo mode is disabled, recheck subscription
                      checkSubscriptionAccess();
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Demo Mode
                  </Typography>
                  {demoMode && (
                    <Chip 
                      label="DEMO" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  )}
                </Box>
              }
            />
            
            {/* Subscription Status */}
            {!hasAnalyticsAccess && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Limited Access:</strong> You're currently viewing demo data. 
                  <Link to="/profile" style={{ marginLeft: '8px', textDecoration: 'none' }}>
                    Upgrade to Student Pro
                  </Link> to access your actual performance analytics.
                </Typography>
              </Alert>
            )}
          </Box>
          
          {/* Demo Mode Notice */}
          {demoMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Demo Mode Active:</strong> You are now viewing data for demo user (demo@student.com) using the same API endpoint. 
                This includes sample test attempts with varying performance levels across different topics. 
                Toggle off to view your actual performance data.
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Student Selection Filter for Teacher/Parent Roles */}
        {user && (user.role === 'teacher' || user.role === 'parent') && (
          <Box mb={3}>
              <Typography variant="h6" gutterBottom>
              {isParent ? 'Select Child' : 'Select Student'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isParent ? 'Choose one of your linked children to view their progress reports' : 'Choose a student to view their analytics'}
            </Typography>
            
            {studentsLoading ? (
              <CircularProgress size={20} />
            ) : studentsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>{studentsError}</Alert>
            ) : availableStudents.length > 0 ? (
              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel id="student-select-label">{isParent ? 'Select Child by Email' : 'Select Student by Email'}</InputLabel>
                <Select
                  labelId="student-select-label"
                  value={selectedStudentId || ''}
                  label={isParent ? 'Select Child by Email' : 'Select Student by Email'}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  sx={{ 
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  {availableStudents.map((student) => (
                    <MenuItem key={student.student_id} value={student.student_id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {student.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.name} • {student.subject}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="info">
                {isParent
                  ? 'No linked children are available yet. Add or accept a child first to view progress reports.'
                  : 'No students available. Add students to your subjects to view their analytics.'}
              </Alert>
            )}
          </Box>
        )}

        {/* Reusable Analytics Dashboard Component */}
                    <AnalyticsDashboard
              analyticsData={analyticsData}
              fromDate={fromDate}
              toDate={toDate}
              selectedTopic={selectedTopic}
              selectedExamMode={selectedExamMode}
              demoMode={demoMode}
              onFromDateChange={setFromDate}
              onToDateChange={setToDate}
              onTopicChange={setSelectedTopic}
              onExamModeChange={setSelectedExamMode}
              availableTopics={availableTopics}
              availableExamModes={availableExamModes}
            />
      </Container>
    );
  }

  return <LayoutComponent>{mainContent}</LayoutComponent>;
};

export default StudentAnalyticsDashboard;
